import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Alert, Spinner, Pagination } from 'react-bootstrap';
import { FaFileInvoice, FaDownload, FaEye, FaExclamationTriangle, FaCheckCircle, FaFilePdf, FaFileCode, FaEnvelope } from 'react-icons/fa';
import { useAuth } from '../components/AuthContext';
import AdminLayout from '../components/AdminLayout';
import api from '../api';
import './AdminTransactionsInvoicesPage.css';

const AdminTransactionsInvoicesPage = () => {
    const { user } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generatingInvoice, setGeneratingInvoice] = useState(false);
    const [resendingEmail, setResendingEmail] = useState(null);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [selectedCharge, setSelectedCharge] = useState(null);
    const [alert, setAlert] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0
    });
    const [filters, setFilters] = useState({
        seller_id: '',
        charge_type: '',
        status: '',
        start_date: '',
        end_date: ''
    });

    // Carica le transazioni
    const loadTransactions = async (page = 1) => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: pagination.limit.toString(),
                ...filters
            });

            const response = await api.get(`/admin/billing/transactions-invoices?${queryParams}`);
            
            if (response.data.success) {
                setTransactions(response.data.data.transactions);
                setPagination(response.data.data.pagination);
            } else {
                setAlert({ type: 'danger', message: response.data.message || 'Errore nel caricamento delle transazioni' });
            }
        } catch (error) {
            console.error('Error loading transactions:', error);
            if (error.response?.status === 401) {
                setAlert({ type: 'danger', message: 'Accesso non autorizzato. Verifica le tue credenziali.' });
            } else {
                setAlert({ type: 'danger', message: 'Errore di connessione al server.' });
            }
        } finally {
            setLoading(false);
        }
    };

    // Genera fattura mancante
    const generateMissingInvoice = async (chargeId) => {
        setGeneratingInvoice(true);
        try {
            const response = await api.post('/admin/billing/generate-missing-invoice', {
                charge_id: chargeId
            });

            if (response.data.success) {
                setAlert({ type: 'success', message: `Fattura ${response.data.invoice.invoice_number} generata con successo!` });
                setShowGenerateModal(false);
                setSelectedCharge(null);
                // Ricarica le transazioni per aggiornare lo stato
                loadTransactions(pagination.page);
            } else {
                setAlert({ type: 'danger', message: response.data.message || 'Errore nella generazione della fattura' });
            }
        } catch (error) {
            console.error('Error generating invoice:', error);
            if (error.response?.status === 401) {
                setAlert({ type: 'danger', message: 'Accesso non autorizzato. Verifica le tue credenziali.' });
            } else {
                setAlert({ type: 'danger', message: 'Errore di connessione al server.' });
            }
        } finally {
            setGeneratingInvoice(false);
        }
    };

    // Apre il PDF in una nuova tab (visualizza)
    const viewInvoicePDF = (invoiceId) => {
        const token = localStorage.getItem('token');
        window.open(`${api.defaults.baseURL}/admin/billing/invoice/${invoiceId}/pdf?inline=true&token=${token}`, '_blank');
    };

    // Scarica il PDF
    const downloadInvoicePDF = async (invoiceId, invoiceNumber) => {
        try {
            const response = await api.get(`/admin/billing/invoice/${invoiceId}/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `fattura_${invoiceNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            setAlert({ type: 'danger', message: 'Errore nel download del PDF.' });
        }
    };

    // Scarica l'XML
    const downloadInvoiceXML = async (invoiceId, invoiceNumber) => {
        try {
            const response = await api.get(`/admin/billing/invoice/${invoiceId}/xml`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `fattura_${invoiceNumber}.xml`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading XML:', error);
            setAlert({ type: 'danger', message: 'Errore nel download dell\'XML.' });
        }
    };

    // Re-invia email fattura a cliente e admin
    const resendInvoiceEmails = async (invoiceId, invoiceNumber) => {
        setResendingEmail(invoiceId);
        try {
            const response = await api.post(`/admin/billing/invoice/${invoiceId}/resend-emails`);
            if (response.data.success) {
                setAlert({ type: 'success', message: `Email re-inviate con successo per la fattura ${invoiceNumber}!` });
            } else {
                setAlert({ type: 'danger', message: response.data.message || 'Errore nel re-invio delle email.' });
            }
        } catch (error) {
            console.error('Error resending emails:', error);
            setAlert({ type: 'danger', message: 'Errore nel re-invio delle email.' });
        } finally {
            setResendingEmail(null);
        }
    };

    // Gestisce i filtri
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const applyFilters = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadTransactions(1);
    };

    const resetFilters = () => {
        setFilters({
            seller_id: '',
            charge_type: '',
            status: '',
            start_date: '',
            end_date: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
        loadTransactions(1);
    };

    // Formatta importo
    const formatAmount = (amount, currency = 'EUR') => {
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    // Formatta data
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('it-IT');
    };

    // Ottiene il badge per lo stato della fattura
    const getInvoiceStatusBadge = (transaction) => {
        if (!transaction.invoice_id) {
            return <Badge bg="danger"><FaExclamationTriangle /> Mancante</Badge>;
        }
        
        switch (transaction.invoice_status) {
            case 'paid':
                return <Badge bg="success"><FaCheckCircle /> Generata</Badge>;
            case 'draft':
                return <Badge bg="warning">Bozza</Badge>;
            case 'sent':
                return <Badge bg="info">Inviata</Badge>;
            default:
                return <Badge bg="secondary">{transaction.invoice_status}</Badge>;
        }
    };

    // Ottiene il badge per il tipo di carica
    const getChargeTypeBadge = (chargeType) => {
        const types = {
            'campaign_creation': { bg: 'primary', text: 'Creazione Campagna' },
            'advertising': { bg: 'info', text: 'Pubblicità' },
            'monthly_subscription': { bg: 'secondary', text: 'Abbonamento' },
            'service_x': { bg: 'warning', text: 'Servizio' }
        };
        
        const type = types[chargeType] || { bg: 'secondary', text: chargeType };
        return <Badge bg={type.bg}>{type.text}</Badge>;
    };

    useEffect(() => {
        loadTransactions();
    }, []);

    return (
        <AdminLayout>
            <div className="admin-transactions-invoices-page">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h1><FaFileInvoice /> Gestione Transazioni e Fatture</h1>
                            </div>

                            {/* Alert */}
                            {alert && (
                                <Alert 
                                    variant={alert.type} 
                                    dismissible 
                                    onClose={() => setAlert(null)}
                                >
                                    {alert.message}
                                </Alert>
                            )}

                            {/* Filtri */}
                            <Card className="mb-4">
                                <Card.Header>
                                    <h5>Filtri</h5>
                                </Card.Header>
                                <Card.Body>
                                    <div className="row">
                                        <div className="col-md-2">
                                            <Form.Group>
                                                <Form.Label>ID Seller</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={filters.seller_id}
                                                    onChange={(e) => handleFilterChange('seller_id', e.target.value)}
                                                    placeholder="ID Seller"
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-2">
                                            <Form.Group>
                                                <Form.Label>Tipo Carica</Form.Label>
                                                <Form.Select
                                                    value={filters.charge_type}
                                                    onChange={(e) => handleFilterChange('charge_type', e.target.value)}
                                                >
                                                    <option value="">Tutti</option>
                                                    <option value="campaign_creation">Creazione Campagna</option>
                                                    <option value="advertising">Pubblicità</option>
                                                    <option value="monthly_subscription">Abbonamento</option>
                                                    <option value="service_x">Servizio</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-2">
                                            <Form.Group>
                                                <Form.Label>Stato</Form.Label>
                                                <Form.Select
                                                    value={filters.status}
                                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                                >
                                                    <option value="">Tutti</option>
                                                    <option value="succeeded">Completato</option>
                                                    <option value="pending">In attesa</option>
                                                    <option value="failed">Fallito</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-2">
                                            <Form.Group>
                                                <Form.Label>Data Inizio</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={filters.start_date}
                                                    onChange={(e) => handleFilterChange('start_date', e.target.value)}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-2">
                                            <Form.Group>
                                                <Form.Label>Data Fine</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={filters.end_date}
                                                    onChange={(e) => handleFilterChange('end_date', e.target.value)}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-2 d-flex align-items-end">
                                            <div className="d-grid gap-2 w-100">
                                                <Button variant="primary" onClick={applyFilters}>
                                                    Applica Filtri
                                                </Button>
                                                <Button variant="outline-secondary" onClick={resetFilters}>
                                                    Reset
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Tabella Transazioni */}
                            <Card>
                                <Card.Header>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5>Transazioni e Fatture</h5>
                                        <div>
                                            <span className="text-muted">
                                                Totale: {pagination.total} transazioni
                                            </span>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {loading ? (
                                        <div className="text-center py-4">
                                            <Spinner animation="border" role="status">
                                                <span className="visually-hidden">Caricamento...</span>
                                            </Spinner>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="table-responsive">
                                                <Table striped hover>
                                                    <thead>
                                                        <tr>
                                                            <th>ID</th>
                                                            <th>Seller</th>
                                                            <th>Importo</th>
                                                            <th>Tipo</th>
                                                            <th>Data</th>
                                                            <th>Stato Fattura</th>
                                                            <th>Azioni</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {transactions.map((transaction) => (
                                                            <tr key={transaction.charge_id}>
                                                                <td>
                                                                    <strong>#{transaction.charge_id}</strong>
                                                                </td>
                                                                <td>
                                                                    <div>
                                                                        <strong>{transaction.business_name || transaction.seller_email}</strong>
                                                                        <br />
                                                                        <small className="text-muted">
                                                                            ID: {transaction.seller_id}
                                                                        </small>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <strong>{formatAmount(transaction.amount, transaction.currency)}</strong>
                                                                </td>
                                                                <td>
                                                                    {getChargeTypeBadge(transaction.charge_type)}
                                                                </td>
                                                                <td>
                                                                    {formatDate(transaction.charge_date)}
                                                                </td>
                                                                <td>
                                                                    {getInvoiceStatusBadge(transaction)}
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex flex-wrap gap-1">
                                                                        {!transaction.invoice_id ? (
                                                                            <Button
                                                                                variant="warning"
                                                                                size="sm"
                                                                                onClick={() => {
                                                                                    setSelectedCharge(transaction);
                                                                                    setShowGenerateModal(true);
                                                                                }}
                                                                            >
                                                                                <FaFileInvoice /> Genera
                                                                            </Button>
                                                                        ) : (
                                                                            <>
                                                                                {transaction.pdf_path && (
                                                                                    <Button
                                                                                        variant="outline-secondary"
                                                                                        size="sm"
                                                                                        title="Visualizza PDF"
                                                                                        onClick={() => viewInvoicePDF(transaction.invoice_id)}
                                                                                    >
                                                                                        <FaEye />
                                                                                    </Button>
                                                                                )}
                                                                                {transaction.pdf_path && (
                                                                                    <Button
                                                                                        variant="danger"
                                                                                        size="sm"
                                                                                        title="Scarica PDF"
                                                                                        onClick={() => downloadInvoicePDF(transaction.invoice_id, transaction.invoice_number)}
                                                                                    >
                                                                                        <FaFilePdf />
                                                                                    </Button>
                                                                                )}
                                                                                {transaction.xml_path && (
                                                                                    <Button
                                                                                        variant="warning"
                                                                                        size="sm"
                                                                                        title="Scarica XML"
                                                                                        onClick={() => downloadInvoiceXML(transaction.invoice_id, transaction.invoice_number)}
                                                                                    >
                                                                                        <FaFileCode />
                                                                                    </Button>
                                                                                )}
                                                                                <Button
                                                                                    variant="primary"
                                                                                    size="sm"
                                                                                    title="Re-invia email a cliente e admin"
                                                                                    onClick={() => resendInvoiceEmails(transaction.invoice_id, transaction.invoice_number)}
                                                                                    disabled={resendingEmail === transaction.invoice_id}
                                                                                >
                                                                                    {resendingEmail === transaction.invoice_id ? (
                                                                                        <Spinner animation="border" size="sm" />
                                                                                    ) : (
                                                                                        <FaEnvelope />
                                                                                    )}
                                                                                </Button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>

                                            {/* Paginazione */}
                                            {pagination.totalPages > 1 && (
                                                <div className="d-flex justify-content-center mt-4">
                                                    <Pagination>
                                                        <Pagination.First 
                                                            onClick={() => loadTransactions(1)}
                                                            disabled={pagination.page === 1}
                                                        />
                                                        <Pagination.Prev 
                                                            onClick={() => loadTransactions(pagination.page - 1)}
                                                            disabled={pagination.page === 1}
                                                        />
                                                        
                                                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                                                            .filter(page => 
                                                                page === 1 || 
                                                                page === pagination.totalPages || 
                                                                Math.abs(page - pagination.page) <= 2
                                                            )
                                                            .map((page, index, array) => (
                                                                <React.Fragment key={page}>
                                                                    {index > 0 && array[index - 1] !== page - 1 && (
                                                                        <Pagination.Ellipsis />
                                                                    )}
                                                                    <Pagination.Item
                                                                        active={page === pagination.page}
                                                                        onClick={() => loadTransactions(page)}
                                                                    >
                                                                        {page}
                                                                    </Pagination.Item>
                                                                </React.Fragment>
                                                            ))
                                                        }
                                                        
                                                        <Pagination.Next 
                                                            onClick={() => loadTransactions(pagination.page + 1)}
                                                            disabled={pagination.page === pagination.totalPages}
                                                        />
                                                        <Pagination.Last 
                                                            onClick={() => loadTransactions(pagination.totalPages)}
                                                            disabled={pagination.page === pagination.totalPages}
                                                        />
                                                    </Pagination>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>
                    </div>
                </div>

                {/* Modal Generazione Fattura */}
                <Modal show={showGenerateModal} onHide={() => setShowGenerateModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Genera Fattura Mancante</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedCharge && (
                            <div>
                                <p>Stai per generare una fattura per la transazione:</p>
                                <ul>
                                    <li><strong>ID Transazione:</strong> #{selectedCharge.charge_id}</li>
                                    <li><strong>Seller:</strong> {selectedCharge.business_name || selectedCharge.seller_email}</li>
                                    <li><strong>Importo:</strong> {formatAmount(selectedCharge.amount, selectedCharge.currency)}</li>
                                    <li><strong>Tipo:</strong> {selectedCharge.charge_type}</li>
                                    <li><strong>Data:</strong> {formatDate(selectedCharge.charge_date)}</li>
                                </ul>
                                <Alert variant="info">
                                    <FaExclamationTriangle /> La fattura verrà generata automaticamente con numero progressivo per l'anno corrente.
                                </Alert>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowGenerateModal(false)}>
                            Annulla
                        </Button>
                        <Button 
                            variant="warning" 
                            onClick={() => generateMissingInvoice(selectedCharge?.charge_id)}
                            disabled={generatingInvoice}
                        >
                            {generatingInvoice ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Generazione...
                                </>
                            ) : (
                                <>
                                    <FaFileInvoice /> Genera Fattura
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </AdminLayout>
    );
};

export default AdminTransactionsInvoicesPage;
